import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { PencilSquareIcon, TrashIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AppIconButton } from './ui';

interface MenuItem {
  id: number;
  name: string;
  price: number;
  description?: string;
}

interface Category {
  id: number;
  name: string;
  description?: string;
  items: MenuItem[];
}

interface CategoryListItemProps {
  category: Category;
  isSelected: boolean;
  isEditing: boolean;
  editForm?: {
    name: string;
    description: string;
  };
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: string, value: string) => void;
  onDelete: () => void;
  onAddItem: () => void;
}

const CategoryListItem: React.FC<CategoryListItemProps> = ({
  category,
  isSelected,
  isEditing,
  editForm,
  onEdit,
  onSave,
  onCancel,
  onChange,
  onDelete,
  onAddItem
}) => {
  const { theme } = useTheme();

  if (isEditing && editForm) {
    return (
      <div className="p-4 space-y-2">
        <input
          type="text"
          value={editForm.name}
          onChange={(e) => onChange('name', e.target.value)}
          className="w-full p-2 rounded"
          style={{
            backgroundColor: theme.components.input.background,
            borderColor: theme.components.input.border,
            color: theme.text
          }}
        />
        <textarea
          value={editForm.description}
          onChange={(e) => onChange('description', e.target.value)}
          className="w-full p-2 rounded resize-none"
          rows={2}
          style={{
            backgroundColor: theme.components.input.background,
            borderColor: theme.components.input.border,
            color: theme.text
          }}
          placeholder="Description (optional)"
        />
        <div className="flex gap-2 justify-end">
          <AppIconButton
            icon={<CheckIcon className="w-4 h-4" />}
            onClick={onSave}
            variant="primary"
            tooltip="Save"
            size="small"
            sx={{ width: 36, height: 36 }}
          />
          <AppIconButton
            icon={<XMarkIcon className="w-4 h-4" />}
            onClick={onCancel}
            variant="secondary"
            tooltip="Cancel"
            size="small"
            sx={{ width: 36, height: 36 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 ${isSelected ? 'font-semibold' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg">{category.name}</h3>
          {category.description && (
            <p className="text-sm opacity-70">{category.description}</p>
          )}
          <p className="text-sm opacity-50 mt-1">{category.items.length} items</p>
        </div>
        <div className="flex gap-1">
          <AppIconButton
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={onAddItem}
            variant="primary"
            tooltip="Add Item"
            size="small"
            sx={{ width: 36, height: 36 }}
          />
          <AppIconButton
            icon={<PencilSquareIcon className="w-4 h-4" />}
            onClick={onEdit}
            variant="secondary"
            tooltip="Edit Category"
            size="small"
            sx={{ width: 36, height: 36 }}
          />
          <AppIconButton
            icon={<TrashIcon className="w-4 h-4" />}
            onClick={onDelete}
            variant="secondary"
            tooltip="Delete Category"
            size="small"
            sx={{ width: 36, height: 36 }}
          />
        </div>
      </div>
    </div>
  );
};

export default CategoryListItem;